import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { TabPanelDisplayMode } from 'app/components/enums/TabPanelDisplayMode';
import { LocalProfileService } from '../../../../services/local-profile.service';
import { LocalProfileResearchService } from '../../../../services/local-profile-research.service';
import { LocalProfileFactory } from '../../../../factories/local-profile.factory';
import { finalize, switchMap } from 'rxjs/operators';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Observable, forkJoin, of, throwError } from 'rxjs';
import { BusinessCitationService } from '../../../../services/business-citation.service';
import { LocalProfileListComponent } from './../local-profile-list/local-profile-list.component';
import { PartnerSsoService } from '../../../../services/partner-sso.service';
import { BusinessCitationSubscriptionsComponent } from '../business-citation-subscriptions/business-citation-subscriptions.component';
import { ResearchData } from '../../../../shared/models/local-profile-research/research-data';
import { CustomerCampaignService } from '../../../../services/customer-campaign.service';
import WebsiteUrlStatuses = Boo.Objects.Enums.WebsiteUrlStatuses;
import LocalProfileRequirements = Boo.LocalProfiles.Models.LocalProfileRequirements

@Component({
  selector: 'app-components-shared-local-profiles',
  templateUrl: './local-profiles.component.html'
})
export class LocalProfilesComponent implements OnInit {
  @ViewChild(LocalProfileListComponent) listComponent: LocalProfileListComponent;
  @ViewChild(BusinessCitationSubscriptionsComponent) subscriptionComponent: BusinessCitationSubscriptionsComponent;

  @Input() customer: Boo.Objects.Customer;
  @Input() localProfileId = 0;
  @Input() readOnly = false;
  @Input() showRegistration = false;
  @Input() showSubscriptions = false;
  @Input() showResearch = false;
  @Input() showResearchMinimum = false;
  @Input() registrationPhoneNumber?: Boo.Objects.AutoSelectedRegistrationPhoneNumber;
  @Input() allowValidationAndAugmentationOrdering = false;
  @Input() requireFullLocalProfileOnAdd = false;
  @Input() requireFullValidation = false;
  @Input() canAddLocalProfile: boolean;
  @Input() createValidationAndAugmentationForNewLocalProfiles = false;
  @Input() showCampaignKeywords = false;

  tabPanelDisplayMode = TabPanelDisplayMode;
  isLoading = false;
  isReadOnly = false;
  shouldShowSubscriptions = false;
  form: UntypedFormGroup;
  localProfileForms: UntypedFormArray;
  localProfiles: Boo.Objects.LocalProfiles.LocalProfile[];
  businessCitationProviderId: number;
  categories: Boo.BusinessCitations.Models.BusinessCitationProviderCategory[];
  profileData: Boo.BusinessCitations.Models.BusinessCitationProviderProfileData;
  partnerRequiresSso = false;
  partnerSsoLabel = '';
  disableBusinessCitationEligibilityControls = false;
  researchData: ResearchData;
  customerCampaignKeywords: string[] = [];
  localProfileRequirements: LocalProfileRequirements;
  requirementErrors: string[] = [];

  constructor(
    private businessCitationService: BusinessCitationService,
    private localProfileFactory: LocalProfileFactory,
    private localProfileService: LocalProfileService,
    private localProfileResearchService: LocalProfileResearchService,
    private partnerSsoService: PartnerSsoService,
    private formBuilder: UntypedFormBuilder,
    private customerCampaignService: CustomerCampaignService) { }

  ngOnInit(): void {
    this.isLoading = true;
    this.partnerRequiresSso = this.partnerSsoService.localProfilesRequireSso(this.customer.PartnerId);
    this.partnerSsoLabel = this.partnerSsoService.getLocalProfileSsoLabel(this.customer.PartnerId);
    this.isReadOnly = this.partnerRequiresSso || this.readOnly;
    this.shouldShowSubscriptions = this.showSubscriptions && !this.localProfileId;
    this.disableBusinessCitationEligibilityControls = !!this.localProfileId;
    this.load()
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({ error: (err) => toastr.error(err) });
  }

  load(): Observable<void> {
    this.localProfileForms = this.formBuilder.array([]);
    this.form = this.formBuilder.group({
      localProfiles: this.localProfileForms
    });
    this.form.valueChanges.subscribe(() => {
      this.updateRequirementErrors();
    });

    // Prevent NG0100: ExpressionChangedAfterItHasBeenCheckedError
    this.localProfileForms.markAsTouched();

    return forkJoin({
      localProfile: this.localProfileId ? this.localProfileService.by(this.localProfileId) : of(null),
      localProfiles: !this.localProfileId ? this.localProfileService.byCustomerId(this.customer.CustomerId) : of([]),
      businessCitationProviderId: this.businessCitationService.getDefaultBusinessCitationProvider(this.customer.CustomerId),
      researchData: (this.showResearch || this.showResearchMinimum) ? this.localProfileResearchService.getLatest(this.customer.CustomerId) : of(null),
      websiteUrls: this.showCampaignKeywords
        // excludeInactiveKeywords: true
        ? this.customerCampaignService.getWebsiteUrls(this.customer.CustomerId, true, [WebsiteUrlStatuses.New, WebsiteUrlStatuses.Supporting, WebsiteUrlStatuses.Target])
        : of([]),
      localProfileRequirements: !this.localProfileId ? this.localProfileService.getRequirements(this.customer.CustomerId) : of(null)
    }).pipe(
      switchMap(result => {
        if (result.localProfile) {
          this.localProfiles = [result.localProfile];
        }
        else {
          this.localProfiles = result.localProfiles.sort(this.localProfileService.sort.bind(this));
          this.localProfileRequirements = result.localProfileRequirements;
          this.updateRequirementErrors();
        }

        this.businessCitationProviderId = result.businessCitationProviderId;
        this.researchData = result.researchData;
        this.customerCampaignKeywords = [...new Set(result.websiteUrls.flatMap(x => x.Keywords.map(y => y.Keyword.Name)))]
          .filter(x => x.length <= 64)
          .sort((a, b) => a.localeCompare(b, 'en', { sensitivity: 'accent' }));
        return forkJoin({
          categories: this.businessCitationService.getBusinessCitationProviderCategories(this.businessCitationProviderId),
          profileData: this.businessCitationService.getBusinessCitationProviderProfileData(this.businessCitationProviderId)
        }).pipe(
          switchMap(result => {
            this.categories = result.categories;
            this.profileData = result.profileData;
            this.localProfiles.forEach(x => { this.localProfileForms.push(this.localProfileFactory.createFullLocalProfileFormGroup(x, this.profileData)); });
            return of(null);
          }));
      })
    );
  }

  saveLocalProfilesButton() {
    this.isLoading = true;
    this.saveLocalProfiles()
      .pipe(
        switchMap(_ => {
          toastr.success('Local Profiles saved');
          return this.refresh();
        }),
        finalize(() => this.isLoading = false))
      .subscribe({ error: (err) => toastr.error(err) });
  }

  saveLocalProfiles(): Observable<void> {
    this.form.markAllAsTouched();
    this.listComponent.updateValueAndValidity();

    this.showRequirementErrors();

    if (this.form.invalid || this.requirementErrors.length > 0) {
      return throwError(() => new Error('Please fix the errors on the page and try again.'));
    }

    const saveableLocalProfiles = this.localProfileFactory.createSaveableLocalProfiles(this.customer,
      this.businessCitationProviderId,
      this.categories,
      this.localProfiles,
      this.localProfileForms,
      this.requireFullLocalProfileOnAdd);

    if (saveableLocalProfiles.length > 0) {
      return this.localProfileService.save({ localProfiles: saveableLocalProfiles, createValidationAndAugmentationForNewLocalProfiles: this.createValidationAndAugmentationForNewLocalProfiles });
    }
    return of(null);
  }

  isValid(): boolean {
    if (!this.partnerRequiresSso) {
      this.form.markAllAsTouched();
      this.listComponent.updateValueAndValidity();
    }
    this.showRequirementErrors();

    return this.requirementErrors.length === 0 && (this.partnerRequiresSso || this.form.valid);
  }

  hasPendingChanges(): boolean {
    return this.form.dirty || this.localProfileForms.dirty;
  }

  doesPartnerRequiresSso(): boolean {
    return this.partnerRequiresSso;
  }

  refreshButton() {
    this.isLoading = true;
    this.refresh()
      .pipe(finalize(() => {
        this.showRequirementErrors();
        this.isLoading = false;
      }))
      .subscribe({ error: (err) => toastr.error(err) });
  }

  refresh(): Observable<void> {
    if (this.listComponent) {
      this.listComponent.reset();
    }

    return this.load().pipe(
      switchMap(_ => {
        if (this.subscriptionComponent) {
          return this.subscriptionComponent.load();
        }
        return of(null);
      })
    );
  }

  add() {
    const localProfileForm = this.localProfileFactory.createNewFormGroup(
      this.customer,
      this.businessCitationProviderId,
      this.profileData,
      this.requireFullLocalProfileOnAdd);
    this.localProfileForms.push(localProfileForm);
    this.listComponent.setActiveLocalProfile(localProfileForm);
  }

  editOnPartnerSite(): void {
    this.isLoading = true;
    this.partnerSsoService.getCustomerSso(this.customer.PartnerId, this.customer.CustomerId)
      .pipe(finalize(() => this.isLoading = false))
      .subscribe(url => {
        if (url) {
          window.open(url, '_blank');
        } else {
          toastr.error('Could not log into partner site. Please ensure sso configuration is correct.');
        }
      });
  }

  getRegistrationNumber(): Boo.Objects.AutoSelectedRegistrationPhoneNumber {
    if (this.localProfileId && this.localProfileForms.length === 1) {
      return this.listComponent.getRegistrationNumber();
    }
    return null;
  }

  private updateRequirementErrors(): void {
    const errors = [];

    if (!this.localProfileRequirements) {
      this.requirementErrors = errors;
      return;
    }

    const activeLocalProfileCount = this.form.get('localProfiles').value.filter(x => x.isActive === true).length;

    if (this.localProfileRequirements.RequiredLocalProfiles > 0 && activeLocalProfileCount !== this.localProfileRequirements.RequiredLocalProfiles) {
      errors.push(`Exactly ${this.localProfileRequirements.RequiredLocalProfiles} active local profile${this.localProfileRequirements.RequiredLocalProfiles !== 1 ? 's' : ''} required`);
    }

    const subscriptionCount = this.form.get('localProfiles').value.filter(x => x.isSubscribed === true).length;

    if (this.localProfileRequirements.RequiredSubscriptions > 0 && subscriptionCount !== this.localProfileRequirements.RequiredSubscriptions) {
      errors.push(`Exactly ${this.localProfileRequirements.RequiredSubscriptions} subscribed location${this.localProfileRequirements.RequiredSubscriptions !== 1 ? 's' : ''} required`);
    } else if (subscriptionCount > this.localProfileRequirements.MaxSubscriptions) {
      errors.push(`Only ${this.localProfileRequirements.MaxSubscriptions} subscribed location${this.localProfileRequirements.MaxSubscriptions !== 1 ? 's' : ''} allowed`);
    }

    this.requirementErrors = errors;
  }

  private showRequirementErrors(): void {
    if (this.requirementErrors.length > 0) {
      toastr.error(this.requirementErrors.join('<br/>'));
    }
  }
}